import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

console.log('Supabase environment check:', {
  url: supabaseUrl ? 'Present' : 'Missing',
  key: supabaseAnonKey ? 'Present' : 'Missing'
});

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing environment variables:', {
    VITE_SUPABASE_URL: supabaseUrl,
    VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? 'Present' : 'Missing'
  });
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Database types (we'll expand these as we go)
export interface Project {
  id: string
  user_id: string
  name: string
  description?: string
  starred?: boolean
  workflow_type: 'enzyme' | 'antibody' | 'peptide'
  created_at: string
  updated_at: string
  team_id?: string
  owner?: {
    first_name?: string
    last_name?: string
    email?: string
    display_name?: string
  }
}

export interface Job {
  id: string
  user_id: string | null
  pdb_file_url: string | null
  smiles: string
  status: string
  name: string
  created_at: string
  updated_at: string
  project_id?: string
  workflow_type?: 'enzyme' | 'antibody' | 'peptide'
  project?: Project // for joined queries
  generation: number
  base_job_id: string
  comments?: string // User comments for this job/generation
}

export interface Result {
  id: string
  job_id: string
  pocket_json_url: string | null
  conserved_residue: string | null
  conserved_json_url: string | null
  active_site_cif_url: string | null
  ligand_smiles: string | null
  neighbors_json_url: string | null
  rfdiffusion_pdb_url: string | null
  finished_at: string | null
  created_at: string
  updated_at: string
  
  // ADD THESE NEW BOLTZ-2 FIELDS:
  confidence_data_url: string | null
  pae_data_url: string | null
  plddt_data_url: string | null
  pde_data_url: string | null
  boltz_results_url: string | null
}

// User Profile types
export interface UserProfile {
  id: string
  email: string
  display_name?: string
  avatar_url?: string
  bio?: string
  created_at: string
  updated_at: string
}

// Team and collaboration types
export interface Team {
  id: string
  name: string
  description?: string
  owner_id: string
  created_at: string
  updated_at: string
}

export interface TeamMember {
  id: string
  team_id: string
  user_id: string
  role: 'owner' | 'admin' | 'member' | 'viewer'
  joined_at: string
  invited_by: string
}

// New Entry types for the refactored structure
export interface Entry {
  id: string
  project_id: string
  user_id: string
  name: string
  content: any // JSONB - typically TextContent
  word_count: number
  position: number
  created_at: string
  updated_at: string
  created_by?: string // Add for real-time collaboration
  updated_by?: string // Add for real-time collaboration
}

export interface DataEntry {
  id: string
  project_id: string
  user_id: string
  name: string
  content: any // JSONB - typically TableContent
  position: number
  created_at: string
  updated_at: string
  created_by?: string // Add for real-time collaboration
  updated_by?: string // Add for real-time collaboration
}

// Content type definitions for JSONB storage
export interface TextContent {
  type: 'rich_text'
  html: string
  pages?: Array<{ id: string; content: string }>
  metadata: {
    version: number
    created_with: string
    last_modified?: string
    collaborators?: number
  }
}

export interface TableContent {
  type: 'data_table'
  notes?: string // Top-level notes for the entry
  tables: Array<{
    id: string
    name: string
    headers: string[]
    rows: string[][]
    rowNames?: string[] // Names for each row
    notes?: string // Notes for this specific table
  }>
  metadata: {
    table_count: number
    version: number
  }
}

// Real-time collaboration types - UPDATED to match useDataEntryRealtime
export interface PresenceUser {
  userId: string
  userName: string
  userColor: string
  online_at: string
}

export interface EditingState {
  userId: string
  userName: string
  userColor: string
  tableId: string
  cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes'
  rowIndex?: number
  colIndex?: number
  timestamp: string
}

// Data entry snapshot for version history (optional table)
export interface DataEntrySnapshot {
  id: string
  entry_id: string
  content: TableContent
  version: number
  created_by: string
  created_at: string
}

// Analytics and tracking types (with display_name)
export interface AnalyticsEvent {
  id: string
  user_id: string
  event_type: string
  event_data: any
  display_name?: string
  created_at: string
}

export interface UserActivity {
  id: string
  user_id: string
  activity_type: string
  activity_data: any
  display_name?: string
  created_at: string
}

// Fix for analytics opt-in type
export interface AnalyticsOptIn {
  auto_backup: boolean
  bio: string | null
  created_at: string | null
  data_collection: boolean
  data_retention: boolean | null
  display_name?: string
}

// Database function return types
export interface DatabaseError {
  message: string
  details?: string
  hint?: string
  code?: string
}

// API Response types
export interface ApiResponse<T> {
  data: T | null
  error: DatabaseError | null
}

// Utility types for queries
export type ProjectWithJobs = Project & {
  jobs: Job[]
}

export type JobWithResults = Job & {
  results: Result[]
}

export type EntryWithProject = Entry & {
  project: Project
}

export type DataEntryWithProject = DataEntry & {
  project: Project
}

// Real-time channel event types
export interface RealtimeEvent<T = any> {
  type: 'INSERT' | 'UPDATE' | 'DELETE'
  table: string
  schema: string
  new?: T
  old?: T
  eventType: 'INSERT' | 'UPDATE' | 'DELETE'
}

// Subscription callback types
export type RealtimeCallback<T = any> = (payload: RealtimeEvent<T>) => void

// Storage types for file uploads
export interface FileUpload {
  file: File
  bucket: string
  path: string
  options?: {
    cacheControl?: string
    contentType?: string
    upsert?: boolean
  }
}

export interface StorageFile {
  name: string
  id: string
  updated_at: string
  created_at: string
  last_accessed_at: string
  metadata: {
    eTag: string
    size: number
    mimetype: string
    cacheControl: string
    lastModified: string
    contentLength: number
    httpStatusCode: number
  }
}

// Utility type for adding display_name to analytics types
export type WithDisplayName<T> = T & { display_name?: string }

// Export the supabase client as default as well
export default supabase